import React from 'react';
import { Link } from 'react-router-dom';
import HtmlContent from '../../../components/common/HtmlContent';
import useHashScroll from '../../../hooks/useHashScroll';

/**
 * FramingPage Component
 * 
 * Displays all problem framing tools content like the legacy HTML site
 */
const FramingPage = () => {
  // Use the hash scroll hook to scroll to the correct section
  useHashScroll();
  
  // Framing intro content from the legacy HTML
  const framingIntroHtml = `
    <p>
    A large part of design thinking revolves around defining and establishing the problem at hand, with focus on empathising with the user. 
    </p>
    
    <p>
    So after discovering and prioritising the problem, it is important to then clearly frame it before moving forwards. There are a number of tools that can help with this. 
    </p>
  `;

  return (
    <>
      {/* Framing Intro */}
      <div id="section-tools-problem-framing" className="tool-overview-container">
        <div className="tool-breadcrumb-container">
          <div className="tool-breadcrumb-wrap">
            <div className="tool-breadcrumb-icon">
              <i className="fas fa-bullhorn fa-2x"></i>
            </div>
            <div className="tool-breadcrumb-nav">
              <Link to="/research">Research Tools</Link>
              &nbsp;&nbsp;
              <i className="fas fa-angle-double-right"></i>
              &nbsp;&nbsp;
              <Link to="/problem">Problem</Link>
            </div>
            <div className="tool-breadcrumb-title">
              <Link to="/problem/framing">Framing</Link>
            </div>
          </div>
        </div>
        <div className="tool-overview-para">
          <HtmlContent html={framingIntroHtml} containerClass="" />
        </div>
        <div className="tool-section-container">
          <div className="tool-section-wrap">
            <div className="tool-section-title">Section Tools</div>
          </div>
          <div className="tool-section-list">
            <div><Link className="tool-section-cta" to="#problem-statement">Problem Statement</Link></div>
            <div><Link className="tool-section-cta" to="#hypothesis">Hypothesis</Link></div>
            <div><Link className="tool-section-cta" to="#problem-framing-worksheet">Problem Framing Worksheet</Link></div>
            <div><Link className="tool-section-cta" to="#problem-framing-sketch">Problem Framing Sketch</Link></div>
            <div><Link className="tool-section-cta" to="#proto-persona">Proto-Persona</Link></div>
          </div>
        </div>
      </div>

      {/* Problem Statement Section */}
      <main className="case-study">
        <span className="case-tool-title" id="problem-statement">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="problem-statement">
            <span className="tool-title-number">
              <Link to="#problem-statement">Problem Statement</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/problem">Problem</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/problem/framing">Framing</Link>
          </div>
          <div className="tool-title-link">
            <Link to="#problem-statement"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-problem-statement" 
            id="tab-problem-statement-overview" 
            defaultChecked
          />
          <label htmlFor="tab-problem-statement-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              <div className="tool-table-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th colSpan="3">Tool Info</th>
                    </tr>
                    <tr>
                      <td>Category</td>
                      <td>Framing</td>  
                    </tr>
                    <tr>
                      <td>Updated</td>
                      <td>10/08/20</td>
                    </tr>
                    <tr>
                      <td>Templates</td>
                      <td>
                        <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                        <a href="/problem/framing/problem-statement.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                        <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                        <a href="https://miro.com/app/board/o9J_kpeg0X8=/" className="link-table">Miro</a>
                      </td>
                    </tr>
                    <tr>
                      <td>Creator</td>
                      <td>N/A</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="tool-opening">
                <p>
                  What is the key problem that is trying to be solved? Empathy with the prospective user, and understanding what the problem might be is extremely important, and a problem statement helps accomplish this. 
                </p>
                <p> 
                  A problem statement further helps avoid coming up with solutions too early on by focusing on the 'why' part of the problem. It is also an excellent way of communicating the problem to stakeholders.
                </p>
                <p> 
                  There are a number of different ways of creating a Problem Statement but the examples below are the more common approach. 
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://productcoalition.com/how-to-write-a-good-customer-problem-statement-a815f80189ba" className="link-table">How to write a real customer problem statement</a><br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.toptal.com/designers/product-design/design-problem-statement" className="link-table">Design Problem Statements – What They Are and How to Frame Them</a><br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.interaction-design.org/literature/article/stage-2-in-the-design-thinking-process-define-the-problem-and-interpret-the-results" className="link-table">Stage 2 in the Design Thinking Process: Define the Problem and Interpret the Results</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-problem-statement" 
            id="tab-problem-statement-template"
          />
          <label htmlFor="tab-problem-statement-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_kpeg0X8=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Problem Statement Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Hypothesis Section */}
      <main className="case-study">
        <span className="case-tool-title" id="hypothesis">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="hypothesis">
            <span className="tool-title-number">
              <Link to="#hypothesis">Hypothesis</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/problem">Problem</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/problem/framing">Framing</Link>
          </div>
          <div className="tool-title-link">
            <Link to="#hypothesis"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-hypothesis" 
            id="tab-hypothesis-overview" 
            defaultChecked
          />
          <label htmlFor="tab-hypothesis-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              <div className="tool-table-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th colSpan="3">Tool Info</th>
                    </tr>
                    <tr>
                      <td>Category</td>
                      <td>Framing</td>  
                    </tr>
                    <tr>
                      <td>Updated</td>
                      <td>10/08/20</td>
                    </tr>
                    <tr>
                      <td>Templates</td>
                      <td>
                        <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                        <a href="/problem/framing/hypothesis.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                        <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                        <a href="https://miro.com/app/board/o9J_kpe3byQ=/?" className="link-table">Miro</a>
                      </td>
                    </tr>
                    <tr>
                      <td>Creator</td>
                      <td>N/A</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="tool-opening">
                <p>
                  Along with a Problem Statement, we can use an Hypothesis as an initial starting point to further investigate. 
                </p>
                <p>
                  This Hypothesis will help us to frame the problem in a way that aligns the product goal with the user goal without proposing exact solutions and testing our assumptions.
                </p>
                <p>
                  The Hypothesis acts as a starting point for future experimentation, and through out the research phase should be revisited and adjusted accordingly to ensure any proposed experience continues to align with the user goal. 
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.interaction-design.org/literature/article/user-research-the-importance-of-hypotheses" className="link-table">User Research – The Importance of Hypotheses</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-hypothesis" 
            id="tab-hypothesis-template"
          />
          <label htmlFor="tab-hypothesis-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_kpe3byQ=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Hypothesis Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Problem Framing Worksheet Section */}
      <main className="case-study">
        <span className="case-tool-title" id="problem-framing-worksheet">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="problem-framing-worksheet">
            <span className="tool-title-number">
              <Link to="#problem-framing-worksheet">Problem Framing Worksheet</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/problem">Problem</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/problem/framing">Framing</Link>
          </div>
          <div className="tool-title-link">
            <Link to="#problem-framing-worksheet"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-problem-framing-worksheet" 
            id="tab-problem-framing-worksheet-overview" 
            defaultChecked
          />
          <label htmlFor="tab-problem-framing-worksheet-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              <div className="tool-table-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th colSpan="3">Tool Info</th>
                    </tr>
                    <tr>
                      <td>Category</td>
                      <td>Framing</td>  
                    </tr>
                    <tr>
                      <td>Updated</td>
                      <td>10/08/20</td>
                    </tr>
                    <tr>
                      <td>Templates</td>
                      <td>
                        <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                        <a href="/problem/framing/problem-framing-worksheet.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                        <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                        <a href="https://miro.com/app/board/o9J_kpeUcnM=/" className="link-table">Miro</a>
                      </td>
                    </tr>
                    <tr>
                      <td>Creator</td>
                      <td><a href="">Mitre.org</a></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="tool-opening">
                <p>
                  With a Problem Framing Worksheet we can begin to gain consensus and clarify the problem the team want to solve. This includes having a sense of what "done" would look like, scoping out the activities and goals and reducing cross-over.
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://itk.mitre.org/problem-framing/" className="link-table">Problem Framing</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-problem-framing-worksheet" 
            id="tab-problem-framing-worksheet-template"
          />
          <label htmlFor="tab-problem-framing-worksheet-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_kpeUcnM=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Problem Framing Worksheet Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Problem Framing Sketch Section */}
      <main className="case-study">
        <span className="case-tool-title" id="problem-framing-sketch">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="problem-framing-sketch">
            <span className="tool-title-number">
              <Link to="#problem-framing-sketch">Problem Framing Sketch</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/problem">Problem</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/problem/framing">Framing</Link>
          </div>
          <div className="tool-title-link">
            <Link to="#problem-framing-sketch"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-problem-framing-sketch" 
            id="tab-problem-framing-sketch-overview" 
            defaultChecked
          />
          <label htmlFor="tab-problem-framing-sketch-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              <div className="tool-table-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th colSpan="3">Tool Info</th>
                    </tr>
                    <tr>
                      <td>Category</td>
                      <td>Framing</td>  
                    </tr>
                    <tr>
                      <td>Updated</td>
                      <td>10/08/20</td>
                    </tr>
                    <tr>
                      <td>Templates</td>
                      <td>
                        <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                        <a href="/problem/framing/problem-framing-sketch.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                        <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                        <a href="https://miro.com/app/board/o9J_kpfLglY=/" className="link-table">Miro</a>
                      </td>
                    </tr>
                    <tr>
                      <td>Creator</td>
                      <td><a href="https://blog.prototypr.io/the-problem-with-problems-and-a-better-way-to-frame-them-visually-b3bb71f30baf">Ben Crothers</a></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="tool-opening">
                <p>
                  A Problem Framing Sketch is an excellent way of visualising a problem. It is also particularly useful as a way to shed assumptions and other perspectives that might overly influence a problem and eventually the solution. 
                </p>
                <p>
                  <a href="https://blog.prototypr.io/the-problem-with-problems-and-a-better-way-to-frame-them-visually-b3bb71f30baf">Ben Crothers</a> sums this up as 3 distinct mindsets;
                  
                  <ul className="bullet-tk">
                      <li>The Journalist - Asking the right questions to understand the problem.</li>
                      <li>The Cook - taking the ingredients and cooking up a Problem Statement.</li>
                      <li>The Visitor - a 'fresh eyed' approached that can ask the 'dumb' questions and bringing a new perspective.</li>
                  </ul>
                </p> 
                <p>
                  The first 2 mindsets are normal, but can still result in a poorly framed problem, but introducing the Visitor Mindset can help cover all aspects of understanding the problem, and using Ben's 'Problem Framing Sketch' will satisfy all of the mindsets.
                </p>
                <p>
                  Include the Problem Statement in the middle (where the ! is) and then include a '5 Whos' (perspective analysis), '5 Whys' (root cause analysis) and '5 So whats' (impact analysis).
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://blog.prototypr.io/the-problem-with-problems-and-a-better-way-to-frame-them-visually-b3bb71f30baf" className="link-table">The problem with problems, and a better way to frame them visually</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-problem-framing-sketch" 
            id="tab-problem-framing-sketch-template"
          />
          <label htmlFor="tab-problem-framing-sketch-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_kpfLglY=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Problem Framing Sketch Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Proto-Persona Section */}
      <main className="case-study">
        <span className="case-tool-title" id="proto-persona">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="proto-persona">
            <span className="tool-title-number">
              <Link to="#proto-persona">Proto-Persona</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/problem">Problem</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/problem/framing">Framing</Link>
          </div>
          <div className="tool-title-link">
            <Link to="#proto-persona"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-proto-persona" 
            id="tab-proto-persona-overview" 
            defaultChecked
          />
          <label htmlFor="tab-proto-persona-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              <div className="tool-table-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th colSpan="3">Tool Info</th>
                    </tr>
                    <tr>
                      <td>Category</td>
                      <td>Framing</td>  
                    </tr>
                    <tr>
                      <td>Updated</td>
                      <td>10/08/20</td>
                    </tr>
                    <tr>
                      <td>Templates</td>
                      <td>
                        <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                        <a href="/problem/framing/proto-persona.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                        <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                        <a href="https://miro.com/app/board/o9J_kpd1bW8=/" className="link-table">Miro</a>
                      </td>
                    </tr>
                    <tr>
                      <td>Creator</td>
                      <td><a href="https://www.userfocus.co.uk/articles/are-personas-past-their-prime.html">Alan Cooper</a></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="tool-opening">
                <p>
                  A Proto-Persona is a pre-research assumption of a specific fictional user that is experiencing the problem. 
                </p>
                <p>
                  By creating an initial vision of the user we can begin to build empathy, understanding and can begin to anchor the problem to real world scenarios. 
                </p>
                <p>    
                  A proto-persona contains four main parts; 
                  
                  <ul className="bullet-tk">
                      <li>Biography & Picture</li>
                      <li>Demographics</li>
                      <li>Needs & Goals</li>
                      <li>Behaviours</li> 
                  </ul>
                </p>
                <p>
                  It is also worth creating multiple Proto-Personas at the same time and deciding which one aligns best with the problem.
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.dtelepathy.com/blog/philosophy/how-to-make-proto-pesonas" className="link-table">How to Make Proto-Personas and Get Everyone on the Same Page</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-proto-persona" 
            id="tab-proto-persona-template"
          />
          <label htmlFor="tab-proto-persona-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_kpd1bW8=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Proto-Persona Template"
            ></iframe>
          </div>
        </div>
      </main>
    </>
  );
};

export default FramingPage; 